import { useCallback, useEffect, useMemo, useState } from 'react';
import useExtendedQuery from './useExtendedQuery';
import { UPCOMING_AUCTIONS_FOR_DISTRIBUTION } from '../graphql/auctions';
import {
    allocatedVolumesOf,
    auctioneerItemKey,
    auctioneerLineKey,
    distributeAuctioneerLines,
    distributeInSteps,
    mergeAuctioneerLines,
    parseVolume,
    sortAuctioneerItems,
} from '../utils/volumeDistribution';
import { createLogger } from '../utils/logging';

// Number of upcoming auctions pre-selected when the modal opens. The user is free
// to select fewer or more of the offered auctions afterwards.
export const PRESELECTED_AUCTION_COUNT = 4;

const logger = createLogger('useVolumeDistribution');

/**
 * Builds the proposed distribution of the cancelled volume. Every auctioneer line is split on its
 * own, so the volume of an auctioneer is redistributed to the very same auctioneer and the auction
 * volumes still add up exactly. An auction without a break down has only its total to split.
 */
const buildProposal = (cancelledItems, cancelledQuantity, count, step) => (
    cancelledItems.length > 0
        ? distributeAuctioneerLines(cancelledItems.map((item) => item.quantity), count, step)
        : { lineShares: [], auctionTotals: distributeInSteps(cancelledQuantity, count, step) }
);

/**
 * Owns everything the 'Distribute Volumes' modal knows: which upcoming auctions can receive the
 * cancelled volume, the proposed split at auction and auctioneer level, what the user has changed
 * since, and whether the result may be submitted. The modal itself only renders it.
 *
 * @param {object} cancelledAuction the cancelled auction whose volume is being distributed
 * @param {number} step every auction and auctioneer volume has to be a multiple of this value
 * @returns {object} the distribution state, its derived flags and the handlers changing it
 */
const useVolumeDistribution = (cancelledAuction, step) => {
    const cancelledQuantity = cancelledAuction.quantity;
    const variables = useMemo(() => ({
        cancelledAuctionId: cancelledAuction.id,
    }), [cancelledAuction.id]);
    const {
        loading,
        error,
        data,
    } = useExtendedQuery(UPCOMING_AUCTIONS_FOR_DISTRIBUTION, {
        variables,
        variablesToUrl: false,
    });
    const upcomingAuctions = useMemo(
        () => data?.upcomingAuctionsForDistribution ?? [],
        [data],
    );
    // Only auctions run by an EU auctioneer are broken down by auctioneer. For every other auction
    // there is nothing to split up and the modal behaves exactly as it did before.
    const cancelledItems = useMemo(
        () => sortAuctioneerItems(data?.cancelledAuction?.auctioneerDataItems ?? []),
        [data],
    );
    const hasAuctioneerBreakdown = cancelledItems.length > 0;

    const [selected, setSelected] = useState({});
    const [volumes, setVolumes] = useState({});
    const [auctioneerVolumes, setAuctioneerVolumes] = useState({});
    // The volumes proposed when the modal opened, so that re-selecting an auction can offer them
    // again instead of making the user retype the whole break down.
    const [proposal, setProposal] = useState({ volumes: {}, auctioneerVolumes: {} });
    const [expanded, setExpanded] = useState({});
    const [initialised, setInitialised] = useState(false);
    const [indivisibleVolume, setIndivisibleVolume] = useState(false);

    const linesOf = useCallback(
        (auction) => mergeAuctioneerLines(auction.auctioneerDataItems, cancelledItems),
        [cancelledItems],
    );
    // A target can only take part when it is broken down by auctioneer exactly as the cancelled
    // auction is: without a break down there is no auctioneer to credit the volume to, and adding
    // to an auction that has one would leave its auctioneer lines no longer adding up.
    const isCompatible = useCallback(
        (auction) => ((auction.auctioneerDataItems ?? []).length > 0) === hasAuctioneerBreakdown,
        [hasAuctioneerBreakdown],
    );
    const distributableAuctions = useMemo(
        () => upcomingAuctions.filter(isCompatible),
        [upcomingAuctions, isCompatible],
    );

    const auctioneerVolumesOfProposal = useCallback((auction, preselectedIndex, lineShares) => {
        const lineVolumes = {};
        linesOf(auction).forEach((line) => {
            const cancelledIndex = cancelledItems
                .findIndex((item) => auctioneerItemKey(item) === auctioneerItemKey(line));
            const lineVolume = preselectedIndex >= 0 && cancelledIndex >= 0
                ? lineShares[cancelledIndex][preselectedIndex]
                : 0;
            lineVolumes[auctioneerLineKey(auction.id, line.auctioneer.memberId, line.submarket.code)] = String(lineVolume);
        });
        return lineVolumes;
    }, [linesOf, cancelledItems]);

    useEffect(() => {
        if (loading || error || initialised || upcomingAuctions.length === 0) {
            return;
        }
        const preselectedCount = Math.min(PRESELECTED_AUCTION_COUNT, distributableAuctions.length);
        const proposed = buildProposal(cancelledItems, cancelledQuantity, preselectedCount, step);
        if (preselectedCount > 0 && !proposed?.auctionTotals) {
            // Auction and auctioneer volumes are required to be multiples of the step, so a
            // cancelled volume that is not means the data is inconsistent and nothing can be
            // proposed.
            logger.error(`Volume ${cancelledQuantity} of cancelled auction ${cancelledAuction.id} cannot be distributed in multiples of ${step}.`);
            setIndivisibleVolume(true);
            setInitialised(true);
            return;
        }
        const { lineShares, auctionTotals } = proposed ?? { lineShares: [], auctionTotals: [] };
        const preselectedIds = new Set(distributableAuctions.slice(0, preselectedCount).map((auction) => auction.id));
        const nextSelected = {};
        const nextVolumes = {};
        let nextAuctioneerVolumes = {};
        upcomingAuctions.forEach((auction) => {
            // A share may come out as zero when the volume is too small to reach every auction,
            // in which case that auction is left unselected rather than selected with no volume.
            const preselectedIndex = preselectedIds.has(auction.id)
                ? distributableAuctions.findIndex((candidate) => candidate.id === auction.id)
                : -1;
            const auctionVolume = preselectedIndex >= 0 ? (auctionTotals[preselectedIndex] ?? 0) : 0;
            nextSelected[auction.id] = auctionVolume > 0;
            nextVolumes[auction.id] = String(auctionVolume);
            if (hasAuctioneerBreakdown && isCompatible(auction)) {
                nextAuctioneerVolumes = {
                    ...nextAuctioneerVolumes,
                    ...auctioneerVolumesOfProposal(auction, preselectedIndex, lineShares),
                };
            }
        });
        setSelected(nextSelected);
        setVolumes(nextVolumes);
        setAuctioneerVolumes(nextAuctioneerVolumes);
        setProposal({ volumes: nextVolumes, auctioneerVolumes: nextAuctioneerVolumes });
        setInitialised(true);
    }, [loading, error, initialised, upcomingAuctions, distributableAuctions, cancelledItems,
        hasAuctioneerBreakdown, isCompatible, auctioneerVolumesOfProposal, cancelledQuantity, step,
        cancelledAuction.id]);

    const selectedAuctions = upcomingAuctions.filter((auction) => selected[auction.id]);
    const parsedVolume = (auctionId) => parseVolume(volumes[auctionId]);
    const auctioneerVolumesOf = (auction) => linesOf(auction).map((line) => ({
        memberId: line.auctioneer.memberId,
        submarketCode: line.submarket.code,
        volume: parseVolume(auctioneerVolumes[auctioneerLineKey(auction.id, line.auctioneer.memberId, line.submarket.code)]),
    }));
    const auctioneerTotalOf = (auction) => auctioneerVolumesOf(auction)
        .reduce((sum, { volume }) => sum + volume, 0);
    const isVolumeValid = (auctionId) => {
        const volume = parsedVolume(auctionId);
        return Number.isInteger(volume) && volume > 0 && volume % step === 0;
    };
    const isAuctioneerVolumeValid = (volume) => Number.isInteger(volume) && volume >= 0 && volume % step === 0;
    const areAuctioneerVolumesValid = (auction) => auctioneerVolumesOf(auction)
        .every(({ volume }) => isAuctioneerVolumeValid(volume));

    // What each auctioneer held on the cancelled auction, and what it is being allocated across the
    // selected auctions. A line the cancelled auction does not carry has nothing to receive, so its
    // cancelled volume is zero and any volume typed into it is a mismatch.
    const cancelledVolumes = useMemo(() => Object.fromEntries(
        cancelledItems.map((item) => [auctioneerItemKey(item), item.quantity]),
    ), [cancelledItems]);
    const allocationEntries = selectedAuctions
        .filter(isCompatible)
        .flatMap((auction) => linesOf(auction).map((line) => ({
            itemKey: auctioneerItemKey(line),
            line,
            rawVolume: auctioneerVolumes[auctioneerLineKey(auction.id, line.auctioneer.memberId, line.submarket.code)],
        })));
    const allocatedVolumes = allocatedVolumesOf(allocationEntries);
    const linesByItemKey = new Map(allocationEntries.map(({ itemKey, line }) => [itemKey, line]));
    const allocationOf = (item) => allocatedVolumes[auctioneerItemKey(item)] ?? { allocated: 0, complete: true };
    const cancelledVolumeOf = (item) => cancelledVolumes[auctioneerItemKey(item)] ?? 0;
    const allocationLines = Object.entries(allocatedVolumes).map(([itemKey, { allocated, complete }]) => {
        const line = linesByItemKey.get(itemKey);
        return {
            name: line.auctioneer.name,
            memberId: line.auctioneer.memberId,
            submarketCode: line.submarket.code,
            allocated,
            complete,
            cancelled: cancelledVolumes[itemKey] ?? 0,
        };
    });

    const noAuctionsAvailable = !loading && !error && upcomingAuctions.length === 0;
    // The volume is still distributed across whatever is available, but the shares are then larger
    // than the user would expect, so the shortfall is pointed out. Auctions that cannot receive the
    // volume at all do not count towards it.
    const fewerAuctionsThanExpected = !loading && !error && distributableAuctions.length > 0
        && distributableAuctions.length < PRESELECTED_AUCTION_COUNT;
    const noDistributableAuctions = !loading && !error && upcomingAuctions.length > 0
        && distributableAuctions.length === 0;
    const hasSelection = selectedAuctions.length > 0;
    const allSelectedValid = hasSelection && selectedAuctions.every((auction) => isVolumeValid(auction.id));
    // The auctioneer lines of an auction always have to add up to its quantity.
    const allAuctioneersValid = !hasAuctioneerBreakdown || selectedAuctions.every((auction) => (
        areAuctioneerVolumesValid(auction) && auctioneerTotalOf(auction) === parsedVolume(auction.id)
    ));
    // The whole cancelled volume has to be placed, and the volume of an auctioneer has to reach that
    // very same auctioneer in full, so both totals are hard rules rather than warnings.
    const distributedTotal = selectedAuctions.reduce((sum, auction) => sum + parsedVolume(auction.id), 0);
    const isTotalMatching = distributedTotal === cancelledQuantity;
    const isAllocationMatching = !hasAuctioneerBreakdown
        || allocationLines.every(({ allocated, cancelled }) => allocated === cancelled);
    const isValid = !loading && !error && !noAuctionsAvailable && !indivisibleVolume && hasSelection
        && allSelectedValid && allAuctioneersValid && isTotalMatching && isAllocationMatching;

    const distributions = selectedAuctions.map((auction) => ({
        auctionId: auction.id,
        volume: parsedVolume(auction.id),
        // Left out entirely for auctions without a break down, so their distribution request stays
        // exactly what it was before auctioneer volumes existed.
        ...(hasAuctioneerBreakdown ? { auctioneerVolumes: auctioneerVolumesOf(auction) } : {}),
    }));

    const onToggle = (auction, isChecked) => {
        // The checkbox re-asserts its state when it is first synchronised with the pre-selection.
        // Acting on that would discard the proposed volumes.
        if (!!selected[auction.id] === isChecked) {
            return;
        }
        setSelected((prev) => ({ ...prev, [auction.id]: isChecked }));
        // Deselecting zeroes the auction out. Selecting offers the originally proposed volumes
        // again - it is the same split the modal opened with, so nothing is rebalanced - and falls
        // back to an empty auction volume for an auction that was never part of a proposal, so that
        // the always visible auction volume field prompts for one.
        const proposedVolume = proposal.volumes[auction.id];
        const hasProposal = isChecked && Number(proposedVolume) > 0;
        let nextVolume = '0';
        if (isChecked) {
            nextVolume = hasProposal ? proposedVolume : '';
        }
        setVolumes((prev) => ({ ...prev, [auction.id]: nextVolume }));
        if (!hasAuctioneerBreakdown) {
            return;
        }
        setAuctioneerVolumes((prev) => {
            const next = { ...prev };
            linesOf(auction).forEach((line) => {
                const key = auctioneerLineKey(auction.id, line.auctioneer.memberId, line.submarket.code);
                // Never left empty: the auctioneer area is collapsed by default, so an empty line
                // would block the distribution through a field the user cannot see. Zero is a valid
                // auctioneer volume, and the resulting mismatch with the auction volume is reported
                // by the modal itself.
                next[key] = hasProposal ? proposal.auctioneerVolumes[key] : '0';
            });
            return next;
        });
    };
    const onVolumeChange = (auctionId, value) => {
        setVolumes((prev) => ({ ...prev, [auctionId]: value }));
    };
    const onAuctioneerVolumeChange = (key, value) => {
        setAuctioneerVolumes((prev) => ({ ...prev, [key]: value }));
    };
    const onToggleExpanded = (auctionId) => {
        setExpanded((prev) => ({ ...prev, [auctionId]: !prev[auctionId] }));
    };

    return {
        loading,
        error,
        upcomingAuctions,
        distributableAuctions,
        hasAuctioneerBreakdown,
        isCompatible,
        linesOf,
        selected,
        volumes,
        auctioneerVolumes,
        expanded,
        initialised,
        indivisibleVolume,
        noAuctionsAvailable,
        noDistributableAuctions,
        fewerAuctionsThanExpected,
        hasSelection,
        selectedAuctions,
        parsedVolume,
        auctioneerTotalOf,
        allocationOf,
        cancelledVolumeOf,
        allocationLines,
        isValid,
        distributions,
        onToggle,
        onVolumeChange,
        onAuctioneerVolumeChange,
        onToggleExpanded,
    };
};

export default useVolumeDistribution;
